import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LangContext";
import { listBlogPosts } from "@/lib/blog";

const SITE_URL = "https://myflovea.com";

const BlogIndex = () => {
  const { lang } = useLang();
  const isFr = lang === "fr";
  const posts = listBlogPosts();

  return (
    <section
      data-testid="blog-index"
      className="max-w-4xl mx-auto px-6 sm:px-10 py-20 md:py-28"
    >
      <Seo
        lang={lang}
        path="/blog"
        title={isFr ? "Journal myFLOVEA — Bien-être & confort au quotidien" : "myFLOVEA Journal — Wellness & everyday comfort"}
        description={
          isFr
            ? "Découvrez nos articles sur le bien-être, la posture, la récupération musculaire et les rituels détente. Conseils pratiques pour prendre soin de votre dos et de votre corps au quotidien."
            : "Discover our articles about wellness, posture, muscle recovery and relaxation rituals. Practical tips to take care of your back and body every day."
        }
      />

      <div className="eyebrow mb-6">{isFr ? "Le journal" : "The journal"}</div>
      <h1
        data-testid="blog-index-title"
        className="font-serif-display text-5xl md:text-6xl leading-[1.05] tracking-tight text-[#1A1A1A]"
      >
        {isFr ? "Bien-être & confort au quotidien" : "Wellness & everyday comfort"}
      </h1>
      <p className="mt-8 text-[17px] leading-relaxed text-[#4a4a4a] max-w-2xl">
        {isFr
          ? "Nos guides pratiques pour prendre soin de votre dos, comprendre les vraies causes de vos tensions, et découvrir des rituels simples qui font une vraie différence — sans changer votre vie."
          : "Our practical guides to take care of your back, understand the real causes of your tensions, and discover simple rituals that make a real difference — without changing your life."}
      </p>

      <div className="mt-14 space-y-8">
        {posts.map((post) => {
          const formattedDate = new Date(post.publishedAt).toLocaleDateString(
            post.lang === "fr" ? "fr-FR" : "en-US",
            { year: "numeric", month: "long", day: "numeric" }
          );
          return (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              data-testid={`blog-post-card-${post.slug}`}
              className="group grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10 items-start border-t border-[#dcd7ce] pt-8"
            >
              <div className="md:col-span-1 aspect-[4/3] bg-[#ebe7df] overflow-hidden rounded-sm">
                <img
                  src={post.hero}
                  alt={post.heroAlt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                />
              </div>
              <div className="md:col-span-2">
                <div className="flex flex-wrap items-center gap-3 text-xs tracking-[0.18em] uppercase text-[#8B6343]">
                  <span>{post.category}</span>
                  <span className="opacity-40">·</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar size={12} strokeWidth={1.6} />
                    {formattedDate}
                  </span>
                  <span className="opacity-40">·</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock size={12} strokeWidth={1.6} />
                    {post.readingMinutes} min
                  </span>
                </div>
                <h2 className="mt-3 font-serif-display text-2xl md:text-3xl leading-tight text-[#1A1A1A] group-hover:opacity-80 transition-opacity">
                  {post.title}
                </h2>
                <p className="mt-3 text-[15px] text-[#4a4a4a] leading-relaxed">
                  {post.excerpt}
                </p>
                <span className="mt-5 inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[#3D2B1F] underline underline-offset-[6px] decoration-[#3D2B1F]/30 group-hover:decoration-[#3D2B1F]">
                  {isFr ? "Lire l'article" : "Read the article"}
                  <ArrowRight size={13} strokeWidth={1.8} />
                </span>
              </div>
            </Link>
          );
        })}
      </div>

      {posts.length === 0 && (
        <p className="mt-16 text-center text-[#4a4a4a]">
          {isFr ? "Aucun article pour l'instant. Revenez bientôt !" : "No articles yet. Come back soon!"}
        </p>
      )}
    </section>
  );
};

export default BlogIndex;
